import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Stack,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { apiClient } from '../../utils/api';
import SafetyConfirmDialog from '../../components/SafetyConfirmDialog';

/** Long-running LLM + large JSON; apiClient default is 5m — override for this flow only. */
const LONG_REQUEST_MS = 900000;

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

/** Format scope item for dropdown: "J44 - Material Requirements Planning (Manufacturing)" */
function formatScopeItemLabel(item) {
  const code = item.scope_item || '';
  const desc = (item.scope_item_description || '').trim();
  const module = (item.module || '').trim();
  const part = desc ? `${code} - ${desc}` : code;
  return module ? `${part} (${module})` : part;
}

/**
 * Build editable rows from API content. Keeps schema aligned with /powerpoint/generate-file.
 */
function contentToSlideRows(content) {
  if (!content) return [];
  if (Array.isArray(content.slides) && content.slides.length >= 2) {
    return content.slides.map((s, idx) => ({
      slide_number: s.slide_number ?? idx + 4,
      title: s.title ?? '',
      body: s.body ?? '',
      kind: idx === 0 ? 'scope' : idx === 1 ? 'e2e' : 'process',
    }));
  }
  const rows = [
    {
      slide_number: 4,
      title: 'Scope Item',
      body: content.slide4_content ?? '',
      kind: 'scope',
    },
    {
      slide_number: 5,
      title: 'End-to-End Process Flow',
      body: content.slide5_content ?? '',
      kind: 'e2e',
    },
  ];
  const subs = content.sub_process_slides || [];
  subs.forEach((p, i) => {
    rows.push({
      slide_number: 6 + i,
      title: p.sub_process_name ?? '',
      body: p.content ?? '',
      kind: 'process',
    });
  });
  return rows;
}

function schedulesHaveGenerating(rows) {
  if (!rows?.length) return false;
  return rows.some((row) =>
    row.scope_items?.some((si) => ['queued', 'generating'].includes(si.presentation?.status))
  );
}

function slideRowsToPresentationPayload(slideRows, baseContent) {
  if (!slideRows || slideRows.length < 2) {
    return {
      ok: false,
      error: 'At least two slides (scope item and end-to-end flow) are required.',
    };
  }
  const s4 = slideRows[0];
  const s5 = slideRows[1];
  const processRows = slideRows.slice(2);
  for (let i = 0; i < processRows.length; i++) {
    const t = (processRows[i].title || '').trim();
    if (!t) {
      return { ok: false, error: `Process slide ${i + 1} must have a title.` };
    }
  }
  const names = processRows.map((r) => (r.title || '').trim());
  const payload = {
    ...baseContent,
    presentation_title: baseContent.presentation_title || '',
    slide4_content: s4.body ?? '',
    slide5_content: s5.body ?? '',
    sub_processes: names,
    decision_table_rows: names.map((n) => ({ process_step: n })),
    sub_process_slides: processRows.map((r) => ({
      sub_process_name: (r.title || '').trim(),
      content: r.body ?? '',
    })),
    slides: [
      { slide_number: 4, title: s4.title || '', body: s4.body ?? '' },
      { slide_number: 5, title: s5.title || '', body: s5.body ?? '' },
      ...processRows.map((r, i) => ({
        slide_number: 6 + i,
        title: r.title || '',
        body: r.body ?? '',
      })),
    ],
  };
  return { ok: true, payload };
}

const FitToStandardPage = () => {
  const { projectId } = useParams();
  const currentProjectId = projectId ? parseInt(projectId, 10) : null;

  // State management
  const [scopeItems, setScopeItems] = useState([]);
  const [selectedScopeItem, setSelectedScopeItem] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [powerPointContent, setPowerPointContent] = useState(null);
  const [editableSlideRows, setEditableSlideRows] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [generatingPPT, setGeneratingPPT] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [schedulePresentationRows, setSchedulePresentationRows] = useState([]);
  const [loadingSchedulePresentations, setLoadingSchedulePresentations] = useState(false);
  const [generateConfirmMode, setGenerateConfirmMode] = useState(null);

  // Fetch scope items for the current project (from URL / navbar)
  useEffect(() => {
    const fetchScopeItems = async () => {
      if (!currentProjectId) {
        setScopeItems([]);
        return;
      }
      try {
        setLoading(true);
        const response = await apiClient.get(`/workshops/scope-items/${currentProjectId}`);
        console.log('Scope items response:', response.data);

        if (response.data) {
          // Handle both array response and object with array property
          if (Array.isArray(response.data)) {
            setScopeItems(response.data);
          } else if (response.data.scope_items && Array.isArray(response.data.scope_items)) {
            setScopeItems(response.data.scope_items);
          } else if (response.data.items && Array.isArray(response.data.items)) {
            setScopeItems(response.data.items);
          } else {
            setScopeItems([]);
            console.warn('Unexpected response format:', response.data);
          }
        } else {
          setScopeItems([]);
        }

        if (response.data && Array.isArray(response.data) && response.data.length === 0) {
          console.log('No scope items found for this project');
        }
      } catch (error) {
        console.error('Error fetching scope items:', error);
        console.error('Error response:', error.response?.data);
        setScopeItems([]);
        if (error.response?.status !== 404) {
          showSnackbar('Error loading scope items', 'error');
        } else {
          // 404 means no scope items found, which is okay
          console.log('No scope items found (404)');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchScopeItems();
  }, [currentProjectId]);

  const fetchSchedulePresentations = React.useCallback(async () => {
    if (!currentProjectId) {
      setSchedulePresentationRows([]);
      return;
    }
    try {
      setLoadingSchedulePresentations(true);
      const response = await apiClient.get(`/explore/powerpoint/workshop-schedule-presentations/${currentProjectId}`);
      setSchedulePresentationRows(response.data?.schedules || []);
    } catch (error) {
      console.error('Error loading workshop schedule presentations:', error);
      setSchedulePresentationRows([]);
    } finally {
      setLoadingSchedulePresentations(false);
    }
  }, [currentProjectId]);

  useEffect(() => {
    fetchSchedulePresentations();
  }, [fetchSchedulePresentations]);

  useEffect(() => {
    if (!schedulesHaveGenerating(schedulePresentationRows)) return undefined;
    const id = setInterval(() => {
      fetchSchedulePresentations();
    }, 8000);
    return () => clearInterval(id);
  }, [schedulePresentationRows, fetchSchedulePresentations]);

  const handleDownloadScheduleAsset = async (assetId, fallbackLabel) => {
    try {
      const response = await apiClient.get(`/explore/powerpoint/workshop-schedule-asset/${assetId}/download`, {
        responseType: 'blob',
        timeout: LONG_REQUEST_MS,
      });
      if (response.status !== 200 || !(response.data instanceof Blob)) {
        showSnackbar('Download failed', 'error');
        return;
      }
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = `${fallbackLabel || 'Fit_to_Standard'}.pptx`;
      const cd = response.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (m && m[1]) filename = m[1].replace(/['"]/g, '').trim();
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Presentation downloaded', 'success');
    } catch (error) {
      console.error('Schedule presentation download:', error);
      showSnackbar(error.response?.data?.error || 'Download failed', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleGenerateContent = async (forceRegenerate = false) => {
    if (!currentProjectId || !selectedScopeItem) {
      showSnackbar('Please select a scope item', 'warning');
      return;
    }

    setGeneratingContent(true);
    try {
      const response = await apiClient.post(
        '/explore/powerpoint/generate-content',
        {
          project_id: currentProjectId,
          scope_item_id: selectedScopeItem.id,
          ...(forceRegenerate ? { force_regenerate: true } : {}),
        },
        { timeout: LONG_REQUEST_MS }
      );

      if (response.data && response.data.success) {
        setPowerPointContent(response.data.content);
        setEditableSlideRows([]);
        setIsEditing(false);
        const ph = response.data.placeholder_processes;
        const fromCache = response.data.from_cache === true;
        if (fromCache) {
          if (Array.isArray(ph) && ph.length > 0) {
            showSnackbar(
              `Loaded saved content. Placeholder text was used for ${ph.length} process slide(s) (you can edit them): ${ph.join('; ')}`,
              'warning'
            );
          } else {
            showSnackbar('Loaded saved presentation content.', 'success');
          }
        } else if (Array.isArray(ph) && ph.length > 0) {
          showSnackbar(
            `Content generated. Placeholder text was used for ${ph.length} process slide(s) (you can edit them): ${ph.join('; ')}`,
            'warning'
          );
        } else {
          showSnackbar('Content generated successfully!', 'success');
        }
      } else {
        showSnackbar(response.data?.error || 'Failed to generate content', 'error');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      showSnackbar(error.response?.data?.error || 'Error generating content', 'error');
    } finally {
      setGeneratingContent(false);
    }
  };

  const handleEditContent = () => {
    setEditableSlideRows(contentToSlideRows(powerPointContent));
    setIsEditing(true);
  };

  const handleSaveEdits = async () => {
    const result = slideRowsToPresentationPayload(editableSlideRows, powerPointContent);
    if (!result.ok) {
      showSnackbar(result.error, 'error');
      return;
    }
    if (!currentProjectId || !selectedScopeItem) {
      showSnackbar('Missing project or scope item', 'error');
      return;
    }
    setSavingEdits(true);
    try {
      const response = await apiClient.put('/explore/powerpoint/cache', {
        project_id: currentProjectId,
        scope_item_id: selectedScopeItem.id,
        content: result.payload,
        placeholder_processes: [],
      });
      if (response.status !== 200 || !response.data?.success) {
        showSnackbar(response.data?.error || 'Failed to save changes', 'error');
        return;
      }
      setPowerPointContent(result.payload);
      setIsEditing(false);
      showSnackbar('Content saved successfully', 'success');
    } catch (error) {
      console.error('Error saving Fit-to-Standard content:', error);
      showSnackbar(error.response?.data?.error || 'Error saving changes', 'error');
    } finally {
      setSavingEdits(false);
    }
  };

  const handleCancelEdit = () => {
    setEditableSlideRows([]);
    setIsEditing(false);
  };

  const updateSlideRow = (index, field, value) => {
    setEditableSlideRows((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addProcessSlide = () => {
    setEditableSlideRows((prev) => {
      const processCount = Math.max(0, prev.length - 2);
      return [
        ...prev,
        {
          slide_number: 6 + processCount,
          title: 'New process',
          body: '',
          kind: 'process',
        },
      ];
    });
  };

  const removeProcessSlide = (index) => {
    if (index < 2) return;
    setEditableSlideRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleGeneratePowerPoint = async () => {
    if (!currentProjectId || !selectedScopeItem || !powerPointContent) {
      showSnackbar('Please generate and approve content first', 'warning');
      return;
    }

    setGeneratingPPT(true);
    try {
      const response = await apiClient.post(
        '/explore/powerpoint/generate-file',
        {
          project_id: currentProjectId,
          scope_item_id: selectedScopeItem.id,
          content: powerPointContent
        },
        { responseType: 'blob', timeout: LONG_REQUEST_MS }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `PowerPoint_${selectedScopeItem.scope_item}_${new Date().toISOString().split('T')[0]}.pptx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('PowerPoint generated and downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error generating PowerPoint:', error);
      showSnackbar(error.response?.data?.error || 'Error generating PowerPoint', 'error');
    } finally {
      setGeneratingPPT(false);
    }
  };

  const slideEditorLabel = (row, index) => {
    const num = index === 0 ? 4 : index === 1 ? 5 : 6 + (index - 2);
    if (row.kind === 'scope') return `Slide ${num} — Scope item`;
    if (row.kind === 'e2e') return `Slide ${num} — End-to-end process flow`;
    return `Slide ${num} — Process`;
  };

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <DescriptionIcon sx={{ color: '#4DB6AC' }} />
            Fit-to-Standard Presentation Generator
          </SectionTitle>
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          Generate AI-powered Fit-to-Standard presentations for workshop scope items
        </Typography>

        {currentProjectId && (
          <Box sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
              <SectionTitle variant="h6" sx={{ mb: 0 }}>
                <CalendarMonthIcon sx={{ color: '#4DB6AC' }} />
                Scheduled workshops — generated decks
              </SectionTitle>
              <IconButton size="small" onClick={() => fetchSchedulePresentations()} aria-label="Refresh workshop decks">
                <RefreshIcon />
              </IconButton>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              When you schedule a workshop (Prepare Phase → Workshop Schedule), we generate the same Fit-to-Standard
              presentations here in the background. You can leave this page; downloads appear when each deck is ready.
            </Typography>
            {loadingSchedulePresentations && schedulePresentationRows.length === 0 ? (
              <LinearProgress sx={{ mb: 1 }} />
            ) : schedulePresentationRows.length === 0 ? (
              <Alert severity="info">
                No workshop schedules yet for this project. Create a schedule from{' '}
                <strong>Prepare Phase → Workshop Schedule</strong>, then generated decks will appear here.
              </Alert>
            ) : (
              <Stack spacing={2}>
                {schedulePresentationRows.map((row) => {
                  const sch = row.schedule || {};
                  const title =
                    sch.workshop_name ||
                    sch.workshop?.workshop_name ||
                    `Workshop ${sch.workshop_id ?? ''}`;
                  const whenParts = [];
                  if (sch.scheduled_date) whenParts.push(sch.scheduled_date);
                  if (sch.start_time && sch.end_time) whenParts.push(`${sch.start_time} – ${sch.end_time}`);
                  const whenStr = whenParts.join(' · ');
                  return (
                    <Card key={sch.id ?? title + whenStr} variant="outlined">
                      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                          <EventAvailableIcon sx={{ color: '#4DB6AC' }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                            {title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {whenStr || '—'}
                            {sch.time_zone ? ` (${sch.time_zone})` : ''}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Scope items covered in this workshop
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Scope item</TableCell>
                                <TableCell width={160}>Status</TableCell>
                                <TableCell align="right" width={200}>
                                  Deck
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(row.scope_items || []).map((siRow, idx) => {
                                const si = siRow.scope_item || {};
                                const pres = siRow.presentation || {};
                                const label = formatScopeItemLabel(si);
                                let chipLabel = pres.status || '—';
                                let chipColor;
                                if (pres.status === 'ready') {
                                  chipLabel = 'Ready';
                                  chipColor = 'success';
                                } else if (pres.status === 'failed') {
                                  chipLabel = 'Failed';
                                  chipColor = 'error';
                                } else if (pres.status === 'generating') {
                                  chipLabel = 'Generating…';
                                  chipColor = 'warning';
                                } else if (pres.status === 'queued') {
                                  chipLabel = 'Queued';
                                  chipColor = 'info';
                                } else if (pres.status === 'none') {
                                  chipLabel = 'Pending';
                                }
                                const dl =
                                  pres.status === 'ready' && pres.id
                                    ? () =>
                                        handleDownloadScheduleAsset(
                                          pres.id,
                                          `Fit_to_Standard_${(si.scope_item || 'scope').replace(/[^\w.-]+/g, '_')}`
                                        )
                                    : null;
                                return (
                                  <TableRow key={`${sch.id}-${si.id ?? idx}`}>
                                    <TableCell>{label}</TableCell>
                                    <TableCell>
                                      <Chip
                                        size="small"
                                        label={chipLabel}
                                        {...(chipColor ? { color: chipColor } : {})}
                                        variant={pres.status === 'ready' ? 'filled' : 'outlined'}
                                      />
                                      {pres.status === 'failed' && pres.error_message ? (
                                        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                          {pres.error_message.length > 120 ? `${pres.error_message.slice(0, 120)}…` : pres.error_message}
                                        </Typography>
                                      ) : null}
                                    </TableCell>
                                    <TableCell align="right">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        disabled={!dl}
                                        onClick={dl || undefined}
                                        sx={{ borderColor: '#4DB6AC', color: '#4DB6AC' }}
                                      >
                                        Download .pptx
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {(!row.scope_items || row.scope_items.length === 0) && (
                          <Typography variant="body2" color="text.secondary">
                            No scope items mapped to this workshop yet.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}
          </Box>
        )}

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {/* Scope Item Selection - project is taken from current project in navbar/URL */}
          <Grid item xs={12}>
            {currentProjectId && scopeItems.length === 0 && !loading ? (
              <Box>
                <FormControl fullWidth disabled>
                  <InputLabel>Select Scope Item</InputLabel>
                  <Select
                    value=""
                    label="Select Scope Item"
                    displayEmpty
                  >
                    <MenuItem disabled value="">
                      <em>No scope items available</em>
                    </MenuItem>
                  </Select>
                </FormControl>
                <Alert severity="info" sx={{ mt: 1 }}>
                  No scope items found for this project. Please upload scope items in the{' '}
                  <strong>Prepare Phase → Workshop Schedule</strong> section first.
                </Alert>
              </Box>
            ) : !currentProjectId ? (
              <Alert severity="info">
                Open a project from the navbar to generate Fit-to-Standard presentations.
              </Alert>
            ) : (
              <Autocomplete
                options={scopeItems}
                getOptionLabel={(option) => formatScopeItemLabel(option)}
                value={selectedScopeItem}
                onChange={(e, newValue) => {
                  setSelectedScopeItem(newValue);
                  setPowerPointContent(null);
                  setEditableSlideRows([]);
                  setIsEditing(false);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={!currentProjectId || loading}
                noOptionsText="No scope items found"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Scope Item"
                    placeholder="Search by code, description or module..."
                    helperText={scopeItems.length > 0 ? `${scopeItems.length} scope item${scopeItems.length !== 1 ? 's' : ''} available` : ''}
                  />
                )}
              />
            )}
          </Grid>

          {/* Generate / Regenerate */}
          <Grid item xs={12}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                startIcon={generatingContent ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
                onClick={() => setGenerateConfirmMode('generate')}
                disabled={!currentProjectId || !selectedScopeItem || generatingContent || loading}
                sx={{ backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#3d9b91' }, flex: 1 }}
                fullWidth
                size="large"
              >
                {generatingContent ? 'Generating Content...' : 'Generate Using AI'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => setGenerateConfirmMode('regenerate')}
                disabled={!currentProjectId || !selectedScopeItem || generatingContent || loading}
                fullWidth
                size="large"
                sx={{ flex: 1 }}
              >
                Regenerate (ignore saved)
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {generatingContent && (
          <Box sx={{ mt: 3 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              Generating presentation content using AI. Scope items with many processes can take several minutes; please keep this page open.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Generated Content Display */}
      {powerPointContent && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <SectionTitle variant="h6">
              <DescriptionIcon />
              Generated Presentation Content
            </SectionTitle>
            {!isEditing && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={handleEditContent}
              >
                Edit
              </Button>
            )}
          </Box>

          {isEditing ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Edit slide titles and content. Add or remove process slides as needed. The structure must stay compatible
                with PowerPoint generation (scope slide, flow slide, then one slide per process).
              </Typography>
              <Stack spacing={2} sx={{ mb: 2 }}>
                {editableSlideRows.map((row, index) => (
                  <Paper key={`slide-edit-${index}`} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="primary">
                        {slideEditorLabel(row, index)}
                      </Typography>
                      {row.kind === 'process' && (
                        <IconButton
                          size="small"
                          aria-label="Remove process slide"
                          onClick={() => removeProcessSlide(index)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    </Stack>
                    <TextField
                      fullWidth
                      label="Title"
                      value={row.title}
                      onChange={(e) => updateSlideRow(index, 'title', e.target.value)}
                      sx={{ mb: 1.5 }}
                      size="small"
                    />
                    <TextField
                      fullWidth
                      label="Content"
                      value={row.body}
                      onChange={(e) => updateSlideRow(index, 'body', e.target.value)}
                      multiline
                      minRows={row.kind === 'process' ? 8 : 4}
                      size="small"
                    />
                  </Paper>
                ))}
              </Stack>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addProcessSlide}
                sx={{ mb: 2 }}
              >
                Add process slide
              </Button>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={savingEdits ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                  onClick={handleSaveEdits}
                  disabled={savingEdits}
                  sx={{ backgroundColor: '#4CAF50' }}
                >
                  {savingEdits ? 'Saving…' : 'Save changes'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancelEdit}
                  disabled={savingEdits}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
          ) : (
            <Box>
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Presentation Title: {powerPointContent.presentation_title || 'N/A'}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Slides ({powerPointContent.slides?.length || 0}):
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Slide #</strong></TableCell>
                          <TableCell><strong>Title</strong></TableCell>
                          <TableCell><strong>Content Preview</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {powerPointContent.slides?.map((slide, index) => (
                          <TableRow key={index}>
                            <TableCell>{slide.slide_number || index + 1}</TableCell>
                            <TableCell>{slide.title || 'N/A'}</TableCell>
                            <TableCell>
                              {slide.body ? (slide.body.substring(0, 100) + (slide.body.length > 100 ? '...' : '')) : 'N/A'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {powerPointContent.decision_table_rows && powerPointContent.decision_table_rows.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Decision Table Process Steps ({powerPointContent.decision_table_rows.length}):
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                        {powerPointContent.decision_table_rows.map((row, index) => (
                          <Chip
                            key={index}
                            label={row.process_step || 'N/A'}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>

              <Button
                variant="contained"
                startIcon={generatingPPT ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleGeneratePowerPoint}
                disabled={generatingPPT}
                sx={{ backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#3d9b91' } }}
                fullWidth
                size="large"
              >
                {generatingPPT ? 'Generating PowerPoint...' : 'Generate & Download PowerPoint'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Snackbar */}
      <SafetyConfirmDialog
        open={generateConfirmMode === 'generate'}
        title="Generate Fit-to-Standard Content?"
        warning="This action uses AI."
        paragraphs={[
          'This action will use AI to generate presentation content for the selected workshop scope item. This may take several minutes and consume significant AI resources.'
        ]}
        confirmLabel="Yes, Generate"
        loading={generatingContent}
        onCancel={() => setGenerateConfirmMode(null)}
        onConfirm={() => { setGenerateConfirmMode(null); handleGenerateContent(false); }}
      />

      <SafetyConfirmDialog
        open={generateConfirmMode === 'regenerate'}
        title="Regenerate and Ignore Saved Content?"
        warning="Saved content for the selected scope item will be bypassed and replaced."
        paragraphs={[
          'This action will bypass the saved content and use AI to generate fresh Fit-to-Standard content.',
          'Any manual edits or previously generated content will be permanently lost.',
          ...(powerPointContent ? [{ text: 'Generate and download the current PowerPoint first if you want to retain a copy.', secondary: true }] : [])
        ]}
        confirmLabel="Yes, Regenerate"
        loading={generatingContent}
        onCancel={() => setGenerateConfirmMode(null)}
        onConfirm={() => { setGenerateConfirmMode(null); handleGenerateContent(true); }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FitToStandardPage;
